/**
 * Column layout resolution: converts user-facing ColumnLayout into DisplayColumn[].
 *
 * Pure logic with no React or DOM dependencies.
 */

import type { ColumnDef, ColumnLayout, Schema } from './types';
import type { DisplayColumn, ResolveDisplayColumns } from '../contracts';

/** Default column widths by type (px). */
export const DEFAULT_WIDTHS: Record<string, number> = {
  number: 110,
  date: 150,
  boolean: 80,
  text: 200,
  array: 220,
  object: 200,
  mixed: 160,
};

export const MIN_WIDTH = 60;
export const MAX_WIDTH = 800;

/**
 * Resolve display columns from schema and layout.
 *
 * Order: pinned (in layout.pinned order) → layout.order (excluding pinned) →
 * schema columns not mentioned in either (appended in schema order).
 * Hidden columns are excluded. Collapsed groups replace their descendants with one entry.
 */
export const resolveDisplayColumns: ResolveDisplayColumns = (
  schema: Schema,
  layout: ColumnLayout
): DisplayColumn[] => {
  const { pinned, order, hidden, collapsedGroups, widths } = layout;

  // Build a set of hidden ids for fast lookup
  const hiddenSet = new Set(hidden);

  // Build a set of collapsed group ids
  const collapsedSet = new Set(collapsedGroups);

  // Track which columns belong to collapsed groups
  const collapsedGroupDescendants = new Map<string, string>(); // columnId → groupId
  for (const groupId of collapsedGroups) {
    const group = schema.groupById.get(groupId);
    if (group) {
      for (const colId of group.columnIds) {
        collapsedGroupDescendants.set(colId, groupId);
      }
    }
  }

  // Build the full ordered list of column ids
  const allIds: string[] = [];
  const seenIds = new Set<string>();

  // 1. Pinned columns first
  for (const id of pinned) {
    if (schema.byId.has(id) && !seenIds.has(id)) {
      allIds.push(id);
      seenIds.add(id);
    }
  }

  // 2. Then order (excluding pinned)
  for (const id of order) {
    if (schema.byId.has(id) && !seenIds.has(id)) {
      allIds.push(id);
      seenIds.add(id);
    }
  }

  // 3. Then schema columns not mentioned in pinned or order (in schema order)
  for (const col of schema.columns) {
    if (!seenIds.has(col.id)) {
      allIds.push(col.id);
      seenIds.add(col.id);
    }
  }

  // Now process the list, handling collapsed groups and hidden columns
  const result: DisplayColumn[] = [];
  const processedGroups = new Set<string>();

  for (const id of allIds) {
    // Skip hidden columns
    if (hiddenSet.has(id)) {
      continue;
    }

    const column = schema.byId.get(id);
    if (!column) continue;

    // Check if this column is part of a collapsed group
    const parentGroupId = collapsedGroupDescendants.get(id);
    if (parentGroupId && collapsedSet.has(parentGroupId)) {
      // This column is part of a collapsed group
      // Add the group entry only once (for the first visible descendant)
      if (!processedGroups.has(parentGroupId)) {
        processedGroups.add(parentGroupId);

        const group = schema.groupById.get(parentGroupId);
        if (!group) continue;

        // Check if ALL descendants are hidden - if so, skip the group entry
        const hasVisibleDescendant = group.columnIds.some(colId => !hiddenSet.has(colId));
        if (!hasVisibleDescendant) {
          continue;
        }

        // Determine if the group is pinned (if its first visible descendant was pinned)
        const firstVisibleDescendant = group.columnIds.find(colId => !hiddenSet.has(colId));
        const isPinned = firstVisibleDescendant ? pinned.includes(firstVisibleDescendant) : false;

        // Compute band and label for the group
        const groupPath = parentGroupId.split('.');
        const band = groupPath.length > 1 ? groupPath[0] : null;
        const label = band ? groupPath.slice(1).join('.') || group.name : group.name;

        // Get width
        const width = getWidth(parentGroupId, widths, 200);

        result.push({
          kind: 'group',
          id: parentGroupId,
          group,
          pinned: isPinned,
          width,
          band,
          label,
        });
      }
      // Skip adding the individual column since it's part of a collapsed group
      continue;
    }

    // Not part of a collapsed group - add as a normal leaf column
    const isPinned = pinned.includes(id);
    const path = column.path;
    const band = path.length > 1 ? path[0] : null;
    const label = band ? path.slice(1).join('.') : column.name;
    const width = getWidth(id, widths, DEFAULT_WIDTHS[column.type] || 200);

    result.push({
      kind: 'leaf',
      id,
      column,
      pinned: isPinned,
      width,
      band,
      label,
    });
  }

  return result;
};

/**
 * Get width for a column or group, clamped to [MIN_WIDTH, MAX_WIDTH].
 */
function getWidth(id: string, widths: Record<string, number>, defaultWidth: number): number {
  const w = widths[id] ?? defaultWidth;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w));
}

/**
 * Returns visible leaf ColumnDefs in display order.
 *
 * Hidden columns are excluded, but columns inside collapsed groups are INCLUDED
 * (because they're still present in the data; the collapse is just a display choice).
 */
export function visibleColumnDefs(schema: Schema, layout: ColumnLayout): ColumnDef[] {
  const { pinned, order, hidden } = layout;
  const hiddenSet = new Set(hidden);

  // Build the full ordered list of column ids
  const allIds: string[] = [];
  const seenIds = new Set<string>();

  // 1. Pinned columns first
  for (const id of pinned) {
    if (schema.byId.has(id) && !seenIds.has(id)) {
      allIds.push(id);
      seenIds.add(id);
    }
  }

  // 2. Then order (excluding pinned)
  for (const id of order) {
    if (schema.byId.has(id) && !seenIds.has(id)) {
      allIds.push(id);
      seenIds.add(id);
    }
  }

  // 3. Then schema columns not mentioned (in schema order)
  for (const col of schema.columns) {
    if (!seenIds.has(col.id)) {
      allIds.push(col.id);
      seenIds.add(col.id);
    }
  }

  // Filter to visible columns and resolve to ColumnDef
  const result: ColumnDef[] = [];
  for (const id of allIds) {
    if (!hiddenSet.has(id)) {
      const col = schema.byId.get(id);
      if (col) {
        result.push(col);
      }
    }
  }

  return result;
}
